"use client";

import { useState, useEffect } from "react";
import { getCoinTransactions, CoinTransaction } from "../../api/referralApi";
import { toast } from "react-toastify";
import { 
  ArrowUpIcon, 
  ArrowDownIcon, 
  ClockIcon,
  CheckCircleIcon 
} from "@heroicons/react/24/outline";

// Map source to display name
const getSourceDisplayName = (source: string): string => {
  const sourceMap: Record<string, string> = {
    google_rate: "Google Review",
    trustpilot_rate: "Trustpilot Review",
    discord_join: "Discord Community",
    extension_rate: "Chrome Extension Review",
    twitter_follow: "Twitter Follow",
    linkedin_post: "LinkedIn Post",
    referral: "Referral Bonus",
    achievement: "Achievement Unlocked",
    purchase: "Coin Purchase",
    redemption: "Premium Redemption",
  };
  
  // If not found in map, capitalize and format the source itself
  if (!sourceMap[source]) {
    return source
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  return sourceMap[source];
};

// Format timestamp to readable format
const formatDate = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface TransactionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
}

const TransactionHistory = ({ isOpen, onClose, currentBalance }: TransactionHistoryProps) => {
  const [transactions, setTransactions] = useState<CoinTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<"ALL" | "CREDIT" | "DEBIT">("ALL");

  useEffect(() => {
    if (isOpen) {
      fetchTransactions();
    }
  }, [isOpen]);

  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      const data = await getCoinTransactions();
      setTransactions(data);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to load transaction history");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter transactions that are settled (settlementTime is in the past)
  const settledTransactions = transactions.filter((txn) => {
    const settlementDate = new Date(txn.settlementTime);
    const currentDate = new Date();
    return settlementDate <= currentDate;
  });

  const filteredTransactions = settledTransactions.filter((txn) => {
    if (filter === "ALL") return true;
    return txn.type === filter;
  });

  // Calculate totals from settled transactions only
  const totalCredits = settledTransactions
    .filter((t) => t.type === "CREDIT")
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalDebits = settledTransactions
    .filter((t) => t.type === "DEBIT")
    .reduce((sum, t) => sum + t.amount, 0);

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div 
        className="fixed inset-0 z-[100] bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl md:p-8 pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading ? (
            <>
              <h2 className="mb-6 text-2xl font-bold text-gray-900">
                Transaction History
              </h2>
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#1C75BC]"></div>
              </div>
            </>
          ) : (
            <>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-gray-900">
            Transaction History
          </h2>
        </div>
        
        {/* Filter buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter("ALL")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === "ALL"
                ? "bg-[#1C75BC] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter("CREDIT")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === "CREDIT"
                ? "bg-green-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Credits
          </button>
          <button
            onClick={() => setFilter("DEBIT")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              filter === "DEBIT"
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Debits
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 p-4">
          <div className="flex items-center gap-2 text-green-700">
            <ArrowUpIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Total Credits</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-green-600">
            +{totalCredits}
          </p>
        </div>
        
        <div className="rounded-lg bg-gradient-to-br from-red-50 to-pink-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <ArrowDownIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Total Debits</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-red-600">
            -{totalDebits}
          </p>
        </div>
        
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 p-4">
          <div className="flex items-center gap-2 text-blue-700">
            <CheckCircleIcon className="h-5 w-5" />
            <span className="text-sm font-medium">Current Balance</span>
          </div>
          <p className="mt-2 text-2xl font-bold text-blue-600">
            {currentBalance}
          </p>
        </div>
      </div>

      {/* Transactions List */}
      {filteredTransactions.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <ClockIcon className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          <p className="text-gray-500">No transactions found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTransactions.map((transaction) => (
            <div
              key={transaction.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 p-4 transition-all hover:border-blue-300 hover:shadow-sm"
            >
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full ${
                    transaction.type === "CREDIT"
                      ? "bg-green-100"
                      : "bg-red-100"
                  }`}
                >
                  {transaction.type === "CREDIT" ? (
                    <ArrowUpIcon className="h-6 w-6 text-green-600" />
                  ) : (
                    <ArrowDownIcon className="h-6 w-6 text-red-600" />
                  )}
                </div>

                {/* Transaction Details */}
                <div>
                  <p className="font-semibold text-gray-900">
                    {getSourceDisplayName(transaction.source)}
                  </p>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      <span>
                        Initiated: {formatDate(transaction.timestamp)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-sm text-gray-500">
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    <span>
                      Settled: {formatDate(transaction.settlementTime)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="text-right">
                <p
                  className={`text-xl font-bold ${
                    transaction.type === "CREDIT"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {transaction.type === "CREDIT" ? "+" : "-"}
                  {transaction.amount}
                </p>
                <p className="text-sm text-gray-500">coins</p>
              </div>
            </div>
          ))}
        </div>
      )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TransactionHistory;
